import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import { TranslateFunction } from 'react-localize-redux';

import { ErrorBoundary } from '../../../../App/ErrorBoundary';
import { MenuItemsList } from '../../../../components/organisms/MenuItemsList';
import { Currency } from '../../../../core/api/general/general.types';
import { MenuItem } from '../../../../core/api/menu/menu.types';
import { flagService } from '../../../../core/services/flagService';
import {
  AddItemHookPayload,
  BasketSource,
} from '../../../../core/types/basket.types';
import { Item as BasketItem } from '../../../../weborder/BasketControl';
import {
  CROSS_SELL_DESKTOP_NUMBER_OF_MENU_ITEMS,
  CROSS_SELL_MOBILE_NUMBER_OF_MENU_ITEMS,
} from './constants';

export type Props = {
  basketItems: BasketItem[];
  className?: string;
  crossSellMenuItems: MenuItem[];
  currency: Currency;
  isLoading: boolean;
  languageCode: string;
  onAddItem: (payload: AddItemHookPayload) => void;
  translate: TranslateFunction;
};

export function CrossSellList(props: Props): JSX.Element {
  const {
    basketItems,
    className = 'fd-cross-sell-list',
    crossSellMenuItems,
    currency,
    isLoading,
    languageCode,
    onAddItem,
    translate,
  } = props;

  const isCrossSellDesktopGridViewEnabled = flagService.isFlagOn(
    'crossSellDesktopGridView',
  );

  const theme = useTheme();
  const isDesktopScreen =
    useMediaQuery(theme.breakpoints.up('lg')) &&
    isCrossSellDesktopGridViewEnabled;
  const menuItemsNumberShown = isDesktopScreen
    ? CROSS_SELL_DESKTOP_NUMBER_OF_MENU_ITEMS
    : CROSS_SELL_MOBILE_NUMBER_OF_MENU_ITEMS;

  const { enqueueSnackbar } = useSnackbar();

  const addedCrossSellItemIds = useRef<MenuItem['MenuItemId'][]>([]);
  const [crossSellList, setCrossSellList] = useState<MenuItem[]>(() => {
    const initialState = crossSellMenuItems.slice(0, menuItemsNumberShown);
    return initialState;
  });

  const showCrossSellList = isLoading || crossSellList.length !== 0;

  useEffect(() => {
    if (crossSellMenuItems.length > 0) {
      filterCrossSellMenuItems();
    }
  }, [crossSellMenuItems.length, menuItemsNumberShown]);

  const filterCrossSellMenuItems = (addedMenuItem?: MenuItem): void => {
    const addedMenuItemsIds = addedMenuItem
      ? [...addedCrossSellItemIds.current, addedMenuItem.MenuItemId]
      : addedCrossSellItemIds.current;
    addedCrossSellItemIds.current = addedMenuItemsIds;

    const filteredCrossSellMenuItems = crossSellMenuItems.filter(
      (item) => !addedMenuItemsIds.includes(item.MenuItemId),
    );
    setCrossSellList(filteredCrossSellMenuItems.slice(0, menuItemsNumberShown));
  };

  const onHandleAddItem = (payload: AddItemHookPayload): void => {
    onAddItem(payload);
    filterCrossSellMenuItems(payload.item);
    enqueueSnackbar(translate('Basket_itemAdded'), {
      variant: 'success',
    });
  };

  if (!showCrossSellList) {
    return null;
  }

  return (
    <ErrorBoundary identifier="cross-sell-list">
      <MenuItemsList
        basketItems={basketItems}
        className={className}
        currency={currency}
        hasGreyBackground
        isLoading={isLoading}
        languageCode={languageCode}
        menuItems={crossSellList}
        onAddItem={onHandleAddItem}
        source={BasketSource.CheckoutCrossSell}
        title={translate('CrossSell_peopleAlsoAdded') as string}
        translate={translate}
        variant={isDesktopScreen ? 'grid' : 'compact'}
      />
    </ErrorBoundary>
  );
}
